import { useState, useEffect } from 'react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface PostTOCProps {
  content: string;
}

function extractToc(html: string): TocItem[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const headings = doc.querySelectorAll('h2, h3');
  const items: TocItem[] = [];
  headings.forEach((el) => {
    const level = parseInt(el.tagName[1], 10);
    const text = el.textContent?.trim() || '';
    const id = el.id || text.replace(/\s+/g, '-').replace(/[^\w\u3040-\u9FFF-]/g, '').toLowerCase();
    if (text) items.push({ id, text, level });
  });
  return items;
}

export function PostTOC({ content }: PostTOCProps) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState('');
  const items = extractToc(content);

  useEffect(() => {
    if (items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0% -70% 0%' }
    );
    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="my-8 border border-[#E8E0D5] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#F8F3EC] hover:bg-[#F0EBE3] transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <i className="ri-list-unordered text-[#5B7B6F] text-sm"></i>
          <span
            className="text-sm font-semibold text-[#2C2416]"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            目次
          </span>
          <span
            className="text-xs text-[#9A8E84]"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            ({items.length}項目)
          </span>
        </div>
        {open
          ? <i className="ri-arrow-up-s-line text-[#9A8E84] text-base"></i>
          : <i className="ri-arrow-down-s-line text-[#9A8E84] text-base"></i>
        }
      </button>

      {open && (
        <nav className="px-5 py-4 bg-[#FDFAF6]">
          <ol className="space-y-1">
            {items.map((item) => (
              <li
                key={item.id}
                style={{ paddingLeft: item.level === 3 ? '1rem' : '0' }}
              >
                <a
                  href={`#${item.id}`}
                  onClick={() => setOpen(false)}
                  className={`block py-1 text-sm leading-snug transition-colors cursor-pointer ${
                    activeId === item.id
                      ? 'text-[#5B7B6F] font-semibold'
                      : 'text-[#7A6E62] hover:text-[#2C2416]'
                  }`}
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {item.level === 3 && (
                    <span className="text-[#C8BFB5] mr-1.5">—</span>
                  )}
                  {item.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}
    </div>
  );
}
